import { useState, useMemo, useImperativeHandle, forwardRef, useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import dayjs from 'lib/dayjs';
import { JustRightHeightModal } from 'components/styled/scoped';
import { officialHistoryV1 } from 'services/api/return';
import SafeImage from 'components/SafeImage';
import type { OfficialHistoryResponseData } from 'services/api/return';
import { T, useTranslate } from '@tolgee/react';

type GetChatHistory<T> = (returnSn: string, returnOrderItemId: string) => Promise<T>;

const ChatHistory = forwardRef((props: { visible: boolean; onCancel: () => void }, ref) => {
  const { visible, onCancel } = props;
  const { t } = useTranslate();
  const [chatList, setChatList] = useState<OfficialHistoryResponseData['historyList']>([]);

  const getChatHistory: GetChatHistory<void> = async (returnSn, returnOrderItemId) => {
    try {
      const data = await officialHistoryV1(returnSn, returnOrderItemId);
      setChatList(data.historyList);
    } catch (err) {
      /** nothing */
    }
  };

  useImperativeHandle(ref, () => {
    return {
      getChatHistory,
    };
  });

  return (
    <JustRightHeightModal
      visible={visible}
      width={528}
      centered
      footer={null}
      title={t('scene.orderWorkspace.returnRefundTab.detail.section.communicationHistory')}
      onCancel={onCancel}
      destroyOnClose
      wrapClassName={css`
        z-index: 1051;
      `}
      maskStyle={{
        zIndex: 1051,
      }}
    >
      {chatList?.map((each) => {
        return (
          <ChatItem key={each.time}>
            <div>
              <div />
              <div>
                {each.picture?.length ? (
                  <T
                    keyName="ViewRefund.谁xx提供了"
                    params={{
                      x: each.operator || '买家',
                      y: dayjs(Number(each.time)).format('YYYY-MM-DD HH:mm:ss'),
                    }}
                  />
                ) : (
                  <T
                    keyName="ViewRefund.谁xx操作了"
                    params={{
                      x: each.operator || '买家',
                      y: dayjs(Number(each.time)).format('YYYY-MM-DD HH:mm:ss'),
                    }}
                  />
                )}
              </div>
            </div>
            <div>
              <div>
                {each.picture.map((imgSrc) => {
                  return <SafeImage src={imgSrc} preview />;
                })}
              </div>
            </div>
          </ChatItem>
        );
      })}
    </JustRightHeightModal>
  );
});

function useChatHistory() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<{
    getChatHistory: GetChatHistory<void>;
  }>();
  const open = (returnSn: string, returnOrderItemId: string) => {
    ref.current?.getChatHistory(returnSn, returnOrderItemId);
    setVisible(true);
  };
  const modal = useMemo(() => {
    return <ChatHistory ref={ref} visible={visible} onCancel={() => setVisible(false)} />;
  }, [visible]);
  return [open, modal] as const;
}

const ChatItem = styled.div`
  &:last-child {
    & > div:nth-child(2) > div {
      border-style: none;
    }
  }
  & > div {
    &:nth-child(1) {
      display: flex;
      align-items: center;
      & > div {
        &:first-child {
          width: 6px;
          height: 6px;
          margin: 0 8px;
          background: #3370ff;
          border-radius: 100%;
        }
        &:last-child {
          color: #515359;
          font-size: 14px;
          height: 20px;
        }
      }
    }
    &:nth-child(2) {
      padding-left: 10px;
      & > div {
        border-left: 2px dashed rgb(211, 215, 224);
        padding: 8px;
      }
    }
  }
`;

export default useChatHistory;
